import { Form } from 'factorial-form'
import { computed, makeObservable } from 'mobx'
import type moment from 'moment'
import type { $Values } from 'utility-types'

import { dateToString, stringToDate } from 'modules/core/lib/dates'

export const CUSTOM_PERIOD_TYPES = Object.freeze({
  monthly: 'monthly',
  semimonthly: 'semimonthly',
  fortnightly: 'fortnightly',
  weekly: 'weekly',
  daily: 'daily',
})

export const PAYROLL_PERIOD_TYPES = Object.freeze({
  current: 'current',
  next: 'next',
})

export const DEFAULT_PERIOD_TYPE = CUSTOM_PERIOD_TYPES.monthly

export const getDefaultPeriodType = ({
  showRealTimeAttendance,
}: {
  showRealTimeAttendance: boolean
}) => (showRealTimeAttendance ? CUSTOM_PERIOD_TYPES.daily : CUSTOM_PERIOD_TYPES.monthly)

export type CustomPeriodTypes = $Values<typeof CUSTOM_PERIOD_TYPES>
export type PayrollPeriodTypes = $Values<typeof PAYROLL_PERIOD_TYPES>

class CustomPeriodForm extends Form {
  constructor(values = {}) {
    super(values, {
      period_type: 'string',
      payroll_period_type: 'string',
      start_on: 'date',
      end_on: 'date',
    })
    makeObservable(this, {
      startOn: computed,
      endOn: computed,
      periodType: computed,
      numberOfDays: computed,
      isMonthly: computed,
      payrollPeriodType: computed,
    })
  }

  get startOn(): moment.Moment {
    return stringToDate(this.get('start_on').value, 'ui')
  }

  get endOn(): moment.Moment {
    return stringToDate(this.get('end_on').value, 'ui')
  }

  get periodType(): CustomPeriodTypes {
    return this.get('period_type').value
  }

  get isMonthly(): boolean {
    return this.periodType === CUSTOM_PERIOD_TYPES.monthly
  }

  get payrollPeriodType(): PayrollPeriodTypes {
    return this.get('payroll_period_type').value
  }

  matches(startOn: moment.Moment, endOn: moment.Moment): boolean {
    return this.startOn.isSame(startOn) && this.endOn.isSame(endOn)
  }

  isInProgress(date: moment.Moment): boolean {
    return this.startOn.isSameOrAfter(date, 'day') || this.endOn.isSameOrAfter(date, 'day')
  }

  get numberOfDays(): number {
    return this.endOn.diff(this.startOn, 'days') + 1
  }

  changePeriod(startOn: moment.Moment, endOn: moment.Moment, periodType: CustomPeriodTypes) {
    this.setValues({
      period_type: periodType,
      payroll_period_type: 'current',
      start_on: dateToString(startOn, 'backend'),
      end_on: dateToString(endOn, 'backend'),
    })
  }
}

export default CustomPeriodForm
